import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, RefreshCw } from 'lucide-react';
import { PERMISSIONS, formatDate } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, StatCard } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DataTable, type Column } from '@/components/ui/table';
import { toast } from 'sonner';

/**
 * Monedas y tipos de cambio.
 *
 * Responde a la pregunta del día a día: **¿con qué tipo se convierte esta moneda y de cuándo es?** Por
 * eso enseña el último tipo de cada moneda con su fecha y su origen, y avisa si la carga del BCE se ha
 * quedado atrás o ha fallado.
 */
interface Moneda {
  code: string; name: string; decimals: number; active: boolean;
  rate: string | null; rate_date: string | null; source: string | null;
}

interface EstadoCarga {
  last_date: string | null; ran_at: string | null; error: string | null; total: number; monedas: number;
}

/** Euros por una unidad, con los decimales que tenga la moneda: es como se lee un tipo de cambio. */
function tipoLegible(rate: string | null): string {
  if (!rate) return '—';
  const valor = Number(rate);
  if (!Number.isFinite(valor)) return '—';
  return `${valor.toLocaleString('es-ES', { minimumFractionDigits: 4, maximumFractionDigits: 8 })} €`;
}

export function CurrenciesPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [cargando, setCargando] = useState(false);

  const monedas = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api<{ data: Moneda[] }>('/currencies')
  });
  const estado = useQuery({
    queryKey: ['exchange-rates-status'],
    queryFn: () => api<EstadoCarga>('/exchange-rates/status')
  });

  const cargarHistorico = async () => {
    setCargando(true);
    try {
      const resultado = await api<{ dias: number; inserted: number; updated: number }>(
        '/exchange-rates/history', { method: 'POST' });
      toast.success(`Histórico de ${resultado.dias} días cargado (${resultado.inserted} tipos nuevos)`);
      void qc.invalidateQueries({ queryKey: ['currencies'] });
      void qc.invalidateQueries({ queryKey: ['exchange-rates-status'] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCargando(false);
    }
  };

  const recargar = async () => {
    setCargando(true);
    try {
      const resultado = await api<{ fecha: string; inserted: number; updated: number }>(
        '/exchange-rates/refresh', { method: 'POST' });
      toast.success(`Tipos del ${formatDate(resultado.fecha)} cargados (${resultado.inserted} nuevos, ${resultado.updated} actualizados)`);
      void qc.invalidateQueries({ queryKey: ['currencies'] });
      void qc.invalidateQueries({ queryKey: ['exchange-rates-status'] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCargando(false);
    }
  };

  const columnas: Column<Moneda>[] = [
    { key: 'code', header: 'Moneda', cell: (fila) => (
      <div className="flex items-center gap-2">
        <span className="font-mono text-[12px]">{fila.code}</span>
        {fila.code === 'EUR' ? <Badge tone="success">Empresa</Badge> : null}
        {fila.active ? null : <Badge tone="warning">Inactiva</Badge>}
      </div>
    ) },
    { key: 'name', header: 'Nombre', cell: (fila) => fila.name },
    { key: 'rate', header: 'Último tipo (euros por unidad)', cell: (fila) => (
      <span className={fila.rate ? 'font-mono text-[12px]' : 'text-muted-foreground'}>
        {fila.rate ? `1 ${fila.code} = ${tipoLegible(fila.rate)}` : '—'}
      </span>
    ) },
    { key: 'date', header: 'Del día', cell: (fila) => fila.rate_date ? formatDate(fila.rate_date) : '—' },
    { key: 'source', header: 'Origen', cell: (fila) => fila.source === 'manual'
      ? <Badge tone="warning">Manual</Badge>
      : fila.source === 'ecb' ? <Badge>BCE</Badge> : '—' }
  ];

  const lista = monedas.data?.data ?? [];
  const conTipo = lista.filter((moneda) => moneda.rate).length;
  const ultima = estado.data;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Monedas"
        description="Con qué tipo se convierte cada moneda a euros y de cuándo es"
        actions={can(PERMISSIONS.FINANCE_WRITE) ? (
          <>
            <Button variant="ghost" onClick={() => void cargarHistorico()} disabled={cargando}>
              Cargar 90 días
            </Button>
            <Button variant="outline" onClick={() => void recargar()} disabled={cargando}>
            <RefreshCw className={`mr-1.5 size-4 ${cargando ? 'animate-spin' : ''}`} /> Actualizar ahora
            </Button>
          </>
        ) : undefined}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Monedas" value={String(ultima?.monedas ?? lista.length)} hint="Activas en el catálogo" icon={Coins} />
        <StatCard label="Con tipo" value={String(conTipo)} hint="El resto se carga con la del BCE" icon={Coins} />
        <StatCard
          label="Última carga"
          value={ultima?.last_date ? formatDate(ultima.last_date) : 'Nunca'}
          hint={ultima?.error ? 'El último intento falló' : 'Automática, de madrugada'}
          tone={ultima?.error ? 'warning' : undefined}
          icon={RefreshCw}
        />
      </div>

      {ultima?.error ? (
        <Card className="border-warning/40 p-3 text-[12px]">
          <p className="font-medium">El último intento de carga falló</p>
          <p className="text-muted-foreground">{ultima.error}</p>
          <p className="mt-1 text-muted-foreground">Mientras tanto se usa el último tipo conocido. Con «Actualizar ahora» se reintenta.</p>
        </Card>
      ) : null}

      {monedas.isError ? <ErrorState message="No se pudieron cargar las monedas" onRetry={() => void monedas.refetch()} /> : null}
      <Card className="p-0">
        <DataTable
          loading={monedas.isLoading}
          columns={columnas}
          rows={lista}
          rowKey={(fila) => fila.code}
          empty={<p className="p-6 text-center text-sm text-muted-foreground">No hay monedas cargadas todavía.</p>}
        />
      </Card>
      <p className="text-[11px] text-muted-foreground">
        Se aplica el último tipo publicado <b>antes</b> del día del documento, así el importe en euros no
        cambia según la hora a la que se registre. <b>Próximamente:</b> pedidos y facturas usarán este tipo y
        enseñarán el importe en su moneda y en euros.
      </p>
    </div>
  );
}
